import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormStyle, getLocaleDayNames, TranslationWidth, WeekDay } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isValidDate, noop, startOfDay } from '../util/helpers';
import { ViewMode } from './view-mode';
import { ZoomDirection } from '../util/zoom.animation';

@Component({
  selector: 'skm-datepicker',
  template: `<div class="skm-datepicker" [ngSwitch]="view">
  <skm-view
    *ngSwitchCase="ViewMode.Days"
    skmDaysStrategy
    class="skm-datepicker-view skm-datepicker-days-view"
    [zoomDirection]="zoomDirection"
    [initialTimestamp]="initialTimestamp"

    [currentTimestamp]="currentTimestamp"
    [selectedTimestamp]="selectedTimestamp"
    [minTimestamp]="minTimestamp"
    [maxTimestamp]="maxTimestamp"

    [deselectEnabled]="deselectEnabled"

    [headingFormat]="headingFormat"
    [weekStart]="weekStart"
    [weekDayLabels]="weekDayLabels"
    [itemFormat]="dayFormat"
    [itemLabels]="dayLabels"

    (headerClick)="switchView($event, ViewMode.Months, 'out')"
    (itemChange)="selectDay($event)"
  ></skm-view>
  <skm-view
    *ngSwitchCase="ViewMode.Months"
    skmMonthsStrategy
    class="skm-datepicker-view skm-datepicker-months-view"
    [zoomDirection]="zoomDirection"
    [initialTimestamp]="initialTimestamp"

    [currentTimestamp]="currentTimestamp"
    [selectedTimestamp]="selectedTimestamp"
    [minTimestamp]="minTimestamp"
    [maxTimestamp]="maxTimestamp"

    [headingFormat]="yearFormat"
    [itemFormat]="monthFormat"
    [itemLabels]="monthLabels"

    (headerClick)="switchView($event, ViewMode.Years, 'out')"
    (itemChange)="switchView($event, ViewMode.Days, 'in')"
  ></skm-view>
  <skm-view
    *ngSwitchCase="ViewMode.Years"
    skmYearsStrategy
    class="skm-datepicker-view skm-datepicker-years-view"
    [zoomDirection]="zoomDirection"
    [initialTimestamp]="initialTimestamp"

    [currentTimestamp]="currentTimestamp"
    [selectedTimestamp]="selectedTimestamp"
    [minTimestamp]="minTimestamp"
    [maxTimestamp]="maxTimestamp"

    [headingFormat]="yearFormat"
    [itemFormat]="yearFormat"

    (itemChange)="switchView($event, ViewMode.Months, 'in')"
  ></skm-view>
</div>
`,
  styles: [`.skm-datepicker{position:relative;overflow:hidden}.skm-datepicker-view{display:block;position:absolute;height:100%;width:100%}`, `button{display:block;color:inherit}button:not([disabled]){cursor:pointer}button[disabled]{pointer-events:none}.skm-datepicker-pane{flex-shrink:0;width:100%;display:flex;flex-direction:column;overflow:hidden}.skm-datepicker-week{display:flex}.skm-datepicker-weekday{width:14.28571%}.skm-datepicker-content{flex:1 0 0;display:flex;flex-direction:column}.skm-datepicker-grid{flex:1 0 0;display:flex;flex-wrap:wrap;align-content:flex-start;pointer-events:none}.skm-datepicker-indent{height:16.66667%}.skm-datepicker-cell{display:flex}:host-context(.skm-datepicker-days-view) .skm-datepicker-cell{width:14.28571%;height:16.66667%}:host-context(.skm-datepicker-months-view) .skm-datepicker-cell{width:33.33333%;height:25%}:host-context(.skm-datepicker-years-view) .skm-datepicker-cell{width:20%;height:25%}.skm-datepicker-item{flex:1;pointer-events:auto}`],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: DatepickerComponent, multi: true},
  ],
})
export class DatepickerComponent implements ControlValueAccessor, OnChanges, OnInit {
  @Input() set date(dirtyDate: Date | number) {
    if (dirtyDate == null) {
      this.selectedTimestamp = undefined;
      return;
    }
    const date = startOfDay(dirtyDate);
    if (date.getTime() !== this.selectedTimestamp) {
      this.selectedTimestamp = isValidDate(date) ? date.getTime() : undefined;
      if (typeof this.selectedTimestamp !== 'undefined') {
        this.initialTimestamp = this.selectedTimestamp;
        this.view = ViewMode.Days;
      }
    }
  }

  @Output() dateChange = new EventEmitter<Date>();

  @Input() set min(dirtyDate: Date | number) {
    const date = startOfDay(dirtyDate);
    this.minTimestamp = isValidDate(date) ? date.valueOf() : undefined;
  }

  @Input() set max(dirtyDate: Date | number) {
    const date = startOfDay(dirtyDate);
    this.maxTimestamp = isValidDate(date) ? date.valueOf() : undefined;
  }

  @Input() deselectEnabled: boolean;

  @Input() dayFormat = 'd';
  @Input() monthFormat = 'MMM';
  @Input() yearFormat = 'y';
  @Input() headingFormat = 'MMMM y';
  @Input() weekStart = WeekDay.Monday;

  @Input() dayLabels: string[];
  @Input() weekDayLabels: string[];
  @Input() monthLabels: string[];

  @Input() view = ViewMode.Days;

  initialTimestamp: number;
  currentTimestamp: number;
  selectedTimestamp: number;
  minTimestamp: number;
  maxTimestamp: number;

  zoomDirection: ZoomDirection;
  readonly ViewMode = ViewMode;

  private onChange: (date: Date) => void = noop;
  private onTouched: () => void = noop;

  constructor(private cd: ChangeDetectorRef, @Inject(LOCALE_ID) private locale: string) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('weekDayLabels' in changes) {
      this.weekDayLabels = this.weekDayLabels || getLocaleDayNames(this.locale, FormStyle.Standalone, TranslationWidth.Abbreviated);
    }
  }

  ngOnInit(): void {
    this.currentTimestamp = startOfDay(new Date()).getTime();
    this.initialTimestamp = this.selectedTimestamp || this.currentTimestamp;
    this.weekDayLabels = this.weekDayLabels || getLocaleDayNames(this.locale, FormStyle.Standalone, TranslationWidth.Abbreviated);
  }

  selectDay(timestamp: number | undefined): void {
    console.log(timestamp);
    this.selectedTimestamp = timestamp;
    const date = (typeof timestamp !== 'undefined') ? new Date(timestamp) : undefined;
    this.onChange(date);
    this.dateChange.emit(date);
  }

  switchView(timestamp: number, view: ViewMode, direction: ZoomDirection) {
    this.zoomDirection = direction;
    setTimeout(() => {
      this.initialTimestamp = timestamp;
      this.view = view;
      this.cd.markForCheck();
    });
  }

  // ControlValueAccessor implementation

  writeValue(obj: any): void {
    this.date = obj;
    this.cd.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
    // TODO implement
  }

  setDisabledState(isDisabled: boolean): void {
    // TODO implement
    this.cd.markForCheck();
  }

}
